import React, { useState } from 'react';
import { Button } from '../ui/button';
import { ShieldCheck, MapPin, Star, Users, Calendar, Phone, MessageSquare, BadgeCheck } from 'lucide-react';
import { Stat } from './sharedAtoms';
import ClaimBusinessModal from './ClaimBusinessModal';
import ShareButton from '../ShareButton';

/** Vendor hero strip: logo, name, GST badge, rating/established/employees stats, CTAs.
 *  Adds: "Verified Business" badge when claimed; "Claim This Business" CTA when unclaimed. */
const SupplierHero = ({ supplier, onEnquire, onSwitchToContact, onClaimed }) => {
  const [claimOpen, setClaimOpen] = useState(false);
  const isClaimed = !!supplier?.claimed;
  const hasMobileOnFile = supplier?.has_mobile_on_file != null
    ? !!supplier.has_mobile_on_file
    : !!(supplier?.mobile && String(supplier.mobile).replace(/\D/g, '').length === 10);

  return (
    <section className="bg-gradient-to-r from-[#2c54b2] to-[#1e3a8a] text-white">
      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row items-start gap-6">
        {supplier.logo_url ? (
          <img src={supplier.logo_url} alt={supplier.name} className="w-24 h-24 rounded-lg object-cover bg-white shrink-0 border-4 border-white/20" />
        ) : (
          <div className="w-24 h-24 rounded-lg bg-white text-[#2c54b2] flex items-center justify-center text-3xl font-bold shrink-0">{supplier.logo}</div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold">{supplier.name}</h1>
            {isClaimed && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/25 border border-emerald-300 text-emerald-50 rounded text-xs font-semibold"
                data-testid="verified-business-badge"
                title="This business owner has verified ownership via OTP"
              >
                <BadgeCheck className="w-3.5 h-3.5" /> Verified Business
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 border border-green-400 text-green-100 rounded text-xs">
              <ShieldCheck className="w-3.5 h-3.5" /> {supplier.gst}
            </span>
          </div>
          <div className="flex items-center gap-2 text-blue-100 mt-2"><MapPin className="w-4 h-4" /> {supplier.city}</div>

          {!isClaimed && (
            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-amber-400/15 border border-amber-300/60 text-amber-50 rounded-md text-xs"
              data-testid="unclaimed-banner"
            >
              <span>Are you the owner of this business?</span>
              <button
                onClick={() => setClaimOpen(true)}
                disabled={!hasMobileOnFile}
                className="font-semibold underline hover:text-white disabled:no-underline disabled:text-amber-200/50 disabled:cursor-not-allowed"
                data-testid="open-claim-modal-btn"
                title={hasMobileOnFile ? 'Claim this business via mobile OTP' : 'No mobile on file — contact admin to claim'}
              >
                {hasMobileOnFile ? 'Claim this business →' : 'Contact admin to claim'}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <Stat label="Rating" value={<span className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{supplier.rating} / 5</span>} />
            <Stat label="Response Rate" value={supplier.response} />
            <Stat label="Established" value={<span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {supplier.established}</span>} />
            <Stat label="Employees" value={<span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {supplier.employees || '—'}</span>} />
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full md:w-auto">
          <Button onClick={onEnquire} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
            <MessageSquare className="w-4 h-4 mr-2" /> Send Enquiry
          </Button>
          <Button onClick={onSwitchToContact} variant="outline" className="bg-white text-[#2c54b2] hover:bg-blue-50 h-11 border-white">
            <Phone className="w-4 h-4 mr-2" /> Contact Vendor
          </Button>
          <ShareButton
            title={supplier.name}
            text={`Check out ${supplier.name}${supplier.city ? ` in ${supplier.city}` : ''} on TelanganaMart`}
            url={`${typeof window !== 'undefined' ? window.location.origin : ''}/api/og/supplier/${supplier.id}`}
            testid="vendor-share"
          />
        </div>
      </div>
      <ClaimBusinessModal
        open={claimOpen}
        onOpenChange={setClaimOpen}
        vendor={supplier}
        onClaimed={onClaimed}
      />
    </section>
  );
};

export default SupplierHero;
